import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import { useAuth } from '../../context/AuthContext';
import ConfirmModal from '../../components/ConfirmModal';
import api from '../../api/axios';
import { Plus, Edit2, Trash2, FolderOpen, X } from 'lucide-react';

const STATUS_OPTIONS = [
  { value: 'aktif', label: 'Aktif', color: '#34c759' },
  { value: 'tamamlandi', label: 'Tamamlandı', color: '#007aff' },
  { value: 'beklemede', label: 'Beklemede', color: '#f97316' },
  { value: 'iptal', label: 'İptal', color: '#ef4444' },
];

const COLORS = ['#007aff','#34c759','#f97316','#ef4444','#8b5cf6','#ec4899','#06b6d4','#eab308'];

export default function AdminProjects() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [confirmModal, setConfirmModal] = React.useState({ open: false, action: null, title: '', message: '' });
  const showConfirm = (title, message, action) => setConfirmModal({ open: true, title, message, action });
  const closeConfirm = () => setConfirmModal({ open: false, action: null, title: '', message: '' });
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', status: 'aktif', color: '#007aff', owner_id: '', department: '', start_date: '', due_date: '' });

  const load = () => api.get('/tasks/projects').then(r => setProjects(r.data));
  useEffect(() => {
    load();
    api.get('/users').then(r => setUsers(r.data.filter(u => u.role === 'admin')));
  }, []);

  const openNew = () => { setEditing(null); setForm({ name: '', description: '', status: 'aktif', color: '#007aff', owner_id: '', department: '', start_date: '', due_date: '' }); setShowModal(true); };
  const openEdit = (p) => { setEditing(p); setForm({ name: p.name, description: p.description || '', status: p.status, color: p.color, owner_id: p.owner_id || '', department: p.department || '', start_date: p.start_date ? p.start_date.substring(0,10) : '', due_date: p.due_date ? p.due_date.substring(0,10) : '' }); setShowModal(true); };

  const save = async () => {
    if (!form.name.trim()) return;
    if (editing) await api.put('/tasks/projects/' + editing.id, form);
    else await api.post('/tasks/projects', form);
    setShowModal(false); load();
  };

  const deleteProject = (id) => {
    showConfirm('Projeyi Sil', 'Bu projeyi silmek istediğinize emin misiniz? Tüm görevler de silinecek!', async () => {
      await api.delete('/tasks/projects/' + id);
      load();
      closeConfirm();
    });
  };

  return (
    <AdminLayout>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ fontWeight: 700, fontSize: 22, color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: 10 }}>
          <FolderOpen size={22} color="#007aff" /> Projeler
        </h2>
        <button onClick={openNew} style={{ background: 'linear-gradient(135deg, #007aff, #5856d6)', color: 'white', border: 'none', borderRadius: 10, padding: '10px 20px', cursor: 'pointer', fontWeight: 600, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Plus size={16} /> Yeni Proje
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14 }}>
        {projects.map(p => {
          const status = STATUS_OPTIONS.find(s => s.value === p.status);
          return (
            <div key={p.id} style={{ background: 'rgba(255,255,255,0.85)', borderRadius: 16, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', border: '1px solid rgba(255,255,255,0.9)' }}>
              <div style={{ height: 6, background: p.color }} />
              <div style={{ padding: '18px 20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#1a1a2e', flex: 1 }}>{p.name}</div>
                  <span style={{ background: (status?.color || '#6b7280') + '20', color: status?.color || '#6b7280', borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 700, flexShrink: 0, marginLeft: 8 }}>{status?.label}</span>
                </div>
                {p.description && <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12, lineHeight: 1.5 }}>{p.description}</div>}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8, marginBottom: 14 }}>
                  {[
                    { label: 'Toplam', value: p.total_tasks, color: '#374151' },
                    { label: 'Devam', value: p.inprogress_tasks, color: '#007aff' },
                    { label: 'Bitti', value: p.completed_tasks, color: '#34c759' },
                    { label: 'Geciken', value: p.overdue_tasks, color: '#ef4444' },
                  ].map(s => (
                    <div key={s.label} style={{ background: '#f9fafb', borderRadius: 8, padding: '8px 4px', textAlign: 'center' }}>
                      <div style={{ fontWeight: 800, fontSize: 16, color: s.color }}>{s.value || 0}</div>
                      <div style={{ fontSize: 9, color: '#9ca3af', fontWeight: 600 }}>{s.label}</div>
                    </div>
                  ))}
                </div>
                {p.owner_name && <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 12 }}>👤 {p.owner_name}</div>}
                {p.due_date && <div style={{ fontSize: 11, color: new Date(p.due_date) < new Date() ? '#ef4444' : '#9ca3af' }}>📅 Son: {new Date(p.due_date).toLocaleDateString('tr-TR')}</div>}
                <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'flex-end' }}>
                  <button onClick={() => openEdit(p)} style={{ background: 'rgba(0,122,255,0.08)', color: '#007aff', border: 'none', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Edit2 size={12} /> Düzenle
                  </button>
                  {isAdmin && <button onClick={() => deleteProject(p.id)} style={{ background: 'rgba(239,68,68,0.08)', color: '#ef4444', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                    <Trash2 size={13} />
                  </button>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}>
          <div style={{ background: 'white', borderRadius: 20, width: '100%', maxWidth: 540, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 32px 80px rgba(0,0,0,0.2)' }}>
            <div style={{ padding: '22px 26px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 18, color: '#1a1a2e' }}>{editing ? 'Projeyi Düzenle' : 'Yeni Proje'}</div>
              <button onClick={() => setShowModal(false)} style={{ background: 'rgba(0,0,0,0.06)', border: 'none', borderRadius: '50%', width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><X size={15} /></button>
            </div>
            <div style={{ padding: '0 26px 26px' }}>
              {[['name','Proje Adı *','text'],['description','Açıklama','text']].map(([key, label, type]) => (
                <div key={key} style={{ marginBottom: 14 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 12, color: '#6b7280' }}>{label.toUpperCase()}</label>
                  <input type={type} value={form[key]} onChange={e => setForm({...form, [key]: e.target.value})}
                    style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                </div>
              ))}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 12, color: '#6b7280' }}>DURUM</label>
                  <select value={form.status} onChange={e => setForm({...form, status: e.target.value})}
                    style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 14, outline: 'none' }}>
                    {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 12, color: '#6b7280' }}>PROJE SAHİBİ</label>
                  <select value={form.owner_id} onChange={e => setForm({...form, owner_id: e.target.value})}
                    style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 14, outline: 'none' }}>
                    <option value="">Seçin...</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 12, color: '#6b7280' }}>BAŞLANGIÇ</label>
                  <input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})}
                    style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 500, fontSize: 12, color: '#6b7280' }}>TESLİM</label>
                  <input type="date" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})}
                    style={{ width: '100%', padding: '10px 14px', border: '1.5px solid #e5e7eb', borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                </div>
              </div>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', marginBottom: 8, fontWeight: 500, fontSize: 12, color: '#6b7280' }}>RENK</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {COLORS.map(c => (
                    <button key={c} onClick={() => setForm({...form, color: c})}
                      style={{ width: 28, height: 28, borderRadius: '50%', background: c, border: form.color === c ? '3px solid #1a1a2e' : '2px solid transparent', cursor: 'pointer' }} />
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button onClick={() => setShowModal(false)} style={{ padding: '10px 20px', border: '1.5px solid #e5e7eb', borderRadius: 10, background: 'white', cursor: 'pointer', fontWeight: 600 }}>İptal</button>
                <button onClick={save} style={{ padding: '10px 24px', background: 'linear-gradient(135deg, #007aff, #5856d6)', color: 'white', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 600 }}>
                  {editing ? 'Güncelle' : 'Oluştur'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ConfirmModal
        isOpen={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={() => { confirmModal.action && confirmModal.action(); }}
        onCancel={closeConfirm}
        confirmText="Evet, Sil"
      />
    </AdminLayout>
  );
}
